import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { type BasePagination } from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import * as services from "../api/services.ts"
import type {
  PermissionDisplayMap,
  PermissionFeatures,
  PermissionLabel,
  PermissionLabelFormData
} from "../types/permissions.types.ts"
import { permissionsSliceName } from "./permissions.constants.ts"

// ─── Labels ──────────────────────────────────────────────────────────────────

export const fetchLabels = createAsyncThunk<
  BasePagination<PermissionLabel>,
  void
>(
  `${permissionsSliceName}/fetchLabels`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getLabels()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response?.data as BasePagination<PermissionLabel>
  }
)

export const fetchDefaultPermissions = createAsyncThunk<
  PermissionFeatures,
  number | string
>(
  `${permissionsSliceName}/fetchDefaultPermissions`,
  async (labelId, { dispatch, rejectWithValue }) => {
    const response = await services.getDefaultPermissions(labelId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    // todo check it
    return response?.data?.permissions as PermissionFeatures
  }
)

export const fetchPermissionsDisplay = createAsyncThunk<
  PermissionDisplayMap,
  void
>(
  `${permissionsSliceName}/fetchPermissionsDisplay`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getPermissionsDisplay()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response?.data as PermissionDisplayMap
  }
)

export const fetchLabelById = createAsyncThunk<
  PermissionLabel & { permissions: PermissionFeatures },
  string | number
>(
  `${permissionsSliceName}/fetchLabelById`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getLabelById(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response?.data as PermissionLabel & {
      permissions: PermissionFeatures
    }
  }
)

export const createLabel = createAsyncThunk<
  PermissionLabel,
  PermissionLabelFormData
>(
  `${permissionsSliceName}/createLabel`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createLabel(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Label created successfully.")

    return response?.data?.details?.data as PermissionLabel
  }
)

export const updateLabel = createAsyncThunk<
  PermissionLabel,
  { id: number; data: Partial<Omit<PermissionLabel, "id">> }
>(
  `${permissionsSliceName}/updateLabel`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.updateLabel(id, data)

    if (!response.ok) {
      const errorDetails = response?.data?.details
      const isBlockingError = !!errorDetails?.blocking

      if (!isBlockingError) dispatch(errorSave(errorDetails))
      return rejectWithValue(errorDetails || { errors: "Unknown error" })
    }

    return response?.data?.details?.data as PermissionLabel
  }
)

export const deleteLabel = createAsyncThunk<void, number>(
  `${permissionsSliceName}/deleteLabel`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteLabel(id)

    if (!response.ok) {
      const errorDetails = response?.data?.details
      const isBlockingError = !!errorDetails?.blocking

      if (!isBlockingError) dispatch(errorSave(errorDetails))
      return rejectWithValue(errorDetails || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Label deleted successfully.")
  }
)

// ─── User Management ─────────────────────────────────────────────────────────

export type ReassignAssignmentsScope =
  | "both"
  | "leads"
  | "projects"
  | "tasks"
  | "all"

export interface ReassignAssignmentsArgs {
  userId: string
  scope: ReassignAssignmentsScope
  target_user_id?: string
}

export const reassignAssignments = createAsyncThunk<
  unknown,
  ReassignAssignmentsArgs
>(
  `${permissionsSliceName}/reassignAssignments`,
  async ({ userId, scope, target_user_id }, { dispatch, rejectWithValue }) => {
    const body: { scope: ReassignAssignmentsScope; target_user_id?: string } = {
      scope
    }
    if (target_user_id) body.target_user_id = target_user_id

    const response = await services.reassignAssignments(userId, body)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response?.data?.details?.data
  }
)

// ─── GCC Ownership Transfer ───────────────────────────────────────────────────

export const transferGccOwnership = createAsyncThunk<
  void,
  { new_owner_user_id: string; fallback_label_id?: number }
>(
  `${permissionsSliceName}/transferGccOwnership`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.transferGccOwnership(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "GCC ownership transferred successfully.")

    return response?.data?.details?.data
  }
)
